import React, { useEffect, useState, useCallback } from 'react';
import { Shield, Plus, Trash2, RefreshCw, AlertTriangle, CheckCircle, Package, ChevronRight, Ban, List } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');

function getHeaders() {
  const token = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

function apiFetch(path, options = {}) {
  return fetch(`${API_URL}/baseline${path}`, { headers: getHeaders(), ...options }).then(async r => {
    const data = await r.json();
    if (!r.ok) throw new Error(data.error || 'Error desconocido');
    return data;
  });
}

const SEVERITY_COLORS = {
  critical: { bg: 'var(--red-dim)', color: 'var(--red)', border: 'var(--red-dim)' },
  high:     { bg: 'var(--amber-dim)', color: 'var(--amber)', border: '#fdba74' },
  medium:   { bg: 'var(--amber-dim)', color: 'var(--amber)', border: '#fde047' },
  low:      { bg: 'var(--green-dim)', color: 'var(--green)', border: '#86efac' },
};

function SeverityBadge({ severity }) {
  const s = SEVERITY_COLORS[severity] || SEVERITY_COLORS.medium;
  const labels = { critical: 'Crítica', high: 'Alta', medium: 'Media', low: 'Baja' };
  return (
    <span style={{ color: s.color, fontSize: 11, fontWeight: 600 }}>
      {labels[severity] || severity}
    </span>
  );
}

function ProfileTypeBadge({ type }) {
  if (type === 'whitelist') {
    return <span style={{ color: 'var(--green)', fontSize: 11, fontWeight: 600 }}>WHITELIST</span>;
  }
  return <span style={{ color: 'var(--red)', fontSize: 11, fontWeight: 600 }}>BLACKLIST</span>;
}

/* ─────────────────────────────── Summary bar ──────────────────────────── */
function SummaryBar() {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    apiFetch('/summary').then(setSummary).catch(() => {});
  }, []);

  if (!summary) return null;

  return (
    <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
      {[
        { label: 'Abiertas', value: summary.total_open, bg: 'var(--red-dim)', color: 'var(--red)' },
        { label: 'Críticas', value: summary.critical, bg: 'var(--red-dim)', color: 'var(--red)' },
        { label: 'Altas', value: summary.high, bg: 'var(--amber-dim)', color: 'var(--amber)' },
        { label: 'Medias', value: summary.medium, bg: 'var(--amber-dim)', color: 'var(--amber)' },
        { label: 'Equipos afectados', value: summary.affected_assets, bg: 'var(--accent-dim)', color: 'var(--accent)' },
      ].map(item => (
        <div key={item.label} style={{ background: item.bg, border: `1px solid ${item.color}30`, borderRadius: 10, padding: '10px 18px', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 90 }}>
          <span style={{ fontSize: 22, fontWeight: 700, color: item.color, lineHeight: 1 }}>{item.value ?? '—'}</span>
          <span style={{ fontSize: 11, color: item.color, marginTop: 2, opacity: 0.8 }}>{item.label}</span>
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────── Perfiles tab ─────────────────────────── */
function ProfilesTab() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [profileSw, setProfileSw] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [editProfile, setEditProfile] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', profile_type: 'blacklist', color: 'var(--accent)', is_active: true });
  const [swForm, setSwForm] = useState({ software_name: '', match_type: 'contains', severity: 'medium', reason: '' });
  const [saving, setSaving] = useState(false);
  const [swSaving, setSwSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    apiFetch('/profiles').then(data => {
      setProfiles(Array.isArray(data) ? data : []);
    }).catch(e => setError(e.message)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadSw = (profileId) => {
    apiFetch(`/profiles/${profileId}/software`).then(data => {
      setProfileSw(prev => ({ ...prev, [profileId]: Array.isArray(data) ? data : [] }));
    }).catch(() => {});
  };

  const toggleExpand = (id) => {
    if (expanded === id) {
      setExpanded(null);
    } else {
      setExpanded(id);
      if (!profileSw[id]) loadSw(id);
    }
  };

  const openNew = () => {
    setEditProfile(null);
    setForm({ name: '', description: '', profile_type: 'blacklist', color: 'var(--accent)', is_active: true });
    setShowModal(true);
  };

  const openEdit = (p) => {
    setEditProfile(p);
    setForm({ name: p.name, description: p.description || '', profile_type: p.profile_type || 'blacklist', color: p.color || 'var(--accent)', is_active: p.is_active !== false });
    setShowModal(true);
  };

  const saveProfile = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (editProfile) {
        await apiFetch(`/profiles/${editProfile.id}`, { method: 'PUT', body: JSON.stringify(form) });
      } else {
        await apiFetch('/profiles', { method: 'POST', body: JSON.stringify(form) });
      }
      setShowModal(false);
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteProfile = async (id) => {
    if (!window.confirm('¿Eliminar este perfil?')) return;
    try {
      await apiFetch(`/profiles/${id}`, { method: 'DELETE' });
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  const addSoftware = async (profileId) => {
    if (!swForm.software_name.trim()) return;
    setSwSaving(true);
    try {
      await apiFetch(`/profiles/${profileId}/software`, { method: 'POST', body: JSON.stringify(swForm) });
      setSwForm({ software_name: '', match_type: 'contains', severity: 'medium', reason: '' });
      loadSw(profileId);
    } catch (e) {
      setError(e.message);
    } finally {
      setSwSaving(false);
    }
  };

  const removeSoftware = async (profileId, swId) => {
    try {
      await apiFetch(`/profiles/${profileId}/software/${swId}`, { method: 'DELETE' });
      loadSw(profileId);
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div>
      {error && (
        <div style={{ background: 'var(--red-dim)', color: 'var(--red)', padding: '10px 14px', borderRadius: 8, marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
          {error}
          <button onClick={() => setError('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red)' }}>✕</button>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button className="btn btn-primary" onClick={openNew}><Plus size={14} style={{ marginRight: 6 }} />Nuevo perfil</button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--gray-500)' }}>Cargando perfiles...</div>
      ) : profiles.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--gray-500)' }}>No hay perfiles definidos.</div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {profiles.map(p => (
            <div key={p.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', padding: '14px 16px', cursor: 'pointer', gap: 12 }}
                onClick={() => toggleExpand(p.id)}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: p.color, flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                    {p.name}
                    <ProfileTypeBadge type={p.profile_type} />
                    {!p.is_active && <span style={{ background: 'var(--border)', color: 'var(--ink-2)', padding: '2px 8px', borderRadius: 12, fontSize: 11 }}>Inactivo</span>}
                  </div>
                  {p.description && <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 2 }}>{p.description}</div>}
                  <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 4 }}>
                    {p.profile_type === 'blacklist' ? 'Software NO permitido' : 'Software que DEBE estar instalado'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 16, fontSize: 12, color: 'var(--gray-500)' }}>
                  <span><Package size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />{p.software_count} software</span>
                  <span><Shield size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />{p.assigned_assets} equipos</span>
                </div>
                <div style={{ display: 'flex', gap: 8 }} onClick={e => e.stopPropagation()}>
                  <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => openEdit(p)}>Editar</button>
                  <button style={{ padding: '4px 10px', fontSize: 12, background: 'var(--red-dim)', color: 'var(--red)', border: 'none', borderRadius: 6, cursor: 'pointer' }} onClick={() => deleteProfile(p.id)}><Trash2 size={12} /></button>
                </div>
                <ChevronRight size={14} style={{ transform: expanded === p.id ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s', color: 'var(--gray-500)' }} />
              </div>

              {expanded === p.id && (
                <div style={{ borderTop: '1px solid var(--border)', padding: 16 }}>
                  <div style={{ fontWeight: 500, fontSize: 13, marginBottom: 10 }}>
                    Software {p.profile_type === 'blacklist' ? 'bloqueado' : 'requerido'}
                  </div>
                  {(profileSw[p.id] || []).length === 0 ? (
                    <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 12 }}>Sin software definido.</div>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12, fontSize: 13 }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                          <th style={{ padding: '6px 8px', fontWeight: 500, color: 'var(--gray-500)' }}>Nombre</th>
                          <th style={{ padding: '6px 8px', fontWeight: 500, color: 'var(--gray-500)' }}>Coincidencia</th>
                          <th style={{ padding: '6px 8px', fontWeight: 500, color: 'var(--gray-500)' }}>Severidad</th>
                          <th style={{ padding: '6px 8px', fontWeight: 500, color: 'var(--gray-500)' }}>Razón</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {(profileSw[p.id] || []).map(sw => (
                          <tr key={sw.id} style={{ borderBottom: '1px solid var(--border)' }}>
                            <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: 12 }}>{sw.software_name}</td>
                            <td style={{ padding: '6px 8px' }}>
                              <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: 12, fontSize: 11 }}>{sw.match_type}</span>
                            </td>
                            <td style={{ padding: '6px 8px' }}><SeverityBadge severity={sw.severity} /></td>
                            <td style={{ padding: '6px 8px', color: 'var(--gray-500)', fontSize: 12 }}>{sw.reason || '—'}</td>
                            <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                              <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red)' }} onClick={() => removeSoftware(p.id, sw.id)}><Trash2 size={13} /></button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  <div style={{ background: 'var(--surface, #f8f9fa)', border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
                    <div style={{ fontWeight: 500, fontSize: 12, marginBottom: 8, color: 'var(--gray-500)' }}>Agregar software</div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <input className="form-input" placeholder="Nombre del software" value={swForm.software_name}
                        onChange={e => setSwForm(f => ({ ...f, software_name: e.target.value }))}
                        style={{ flex: '2 1 180px', minWidth: 0 }} />
                      <select className="form-input" value={swForm.match_type}
                        onChange={e => setSwForm(f => ({ ...f, match_type: e.target.value }))}
                        style={{ flex: '1 1 110px', minWidth: 0 }}>
                        <option value="contains">contains</option>
                        <option value="exact">exact</option>
                        <option value="regex">regex</option>
                      </select>
                      <select className="form-input" value={swForm.severity}
                        onChange={e => setSwForm(f => ({ ...f, severity: e.target.value }))}
                        style={{ flex: '1 1 100px', minWidth: 0 }}>
                        <option value="critical">Crítica</option>
                        <option value="high">Alta</option>
                        <option value="medium">Media</option>
                        <option value="low">Baja</option>
                      </select>
                      <input className="form-input" placeholder="Razón" value={swForm.reason}
                        onChange={e => setSwForm(f => ({ ...f, reason: e.target.value }))}
                        style={{ flex: '2 1 160px', minWidth: 0 }} />
                      <button className="btn btn-primary" style={{ whiteSpace: 'nowrap' }}
                        onClick={() => addSoftware(p.id)} disabled={swSaving}>
                        <Plus size={13} style={{ marginRight: 4 }} />Agregar
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="card" style={{ width: 460, padding: 24 }}>
            <h3 style={{ marginBottom: 16, fontWeight: 600 }}>{editProfile ? 'Editar perfil' : 'Nuevo perfil'}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Nombre *</label>
                <input className="form-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={{ width: '100%' }} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Descripción</label>
                <textarea className="form-input" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  style={{ width: '100%', resize: 'vertical', minHeight: 72 }} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Tipo de perfil</label>
                <select className="form-input" value={form.profile_type} onChange={e => setForm(f => ({ ...f, profile_type: e.target.value }))} style={{ width: '100%' }}>
                  <option value="blacklist">Blacklist — software NO permitido</option>
                  <option value="whitelist">Whitelist — software que DEBE estar instalado</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Color</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="color" value={form.color} onChange={e => setForm(f => ({ ...f, color: e.target.value }))}
                    style={{ width: 40, height: 36, border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer', padding: 2 }} />
                  <input className="form-input" value={form.color} onChange={e => setForm(f => ({ ...f, color: e.target.value }))} style={{ flex: 1 }} />
                </div>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
                Perfil activo
              </label>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={saveProfile} disabled={saving || !form.name.trim()}>
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────── Asignaciones tab ──────────────────────── */
function AssignmentsTab() {
  const [profiles, setProfiles] = useState([]);
  const [assets, setAssets] = useState([]);
  const [assetProfiles, setAssetProfiles] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      apiFetch('/profiles'),
      fetch(`${API_URL}/assets`, { headers: getHeaders() }).then(r => r.json()),
    ]).then(([p, assetsResp]) => {
      setProfiles(Array.isArray(p) ? p.filter(x => x.is_active) : []);
      const list = assetsResp && Array.isArray(assetsResp.data) ? assetsResp.data : (Array.isArray(assetsResp) ? assetsResp : []);
      setAssets(list);
      // Load profile for each asset
      return Promise.all(list.map(a =>
        apiFetch(`/assets/${a.id}/profile`).then(prof => ({ assetId: a.id, profile: prof })).catch(() => ({ assetId: a.id, profile: null }))
      ));
    }).then(results => {
      const map = {};
      results.forEach(r => { map[r.assetId] = r.profile; });
      setAssetProfiles(map);
    }).catch(e => setError(e.message)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const assignProfile = async (assetId, profileId) => {
    setSaving(assetId);
    try {
      await apiFetch(`/assets/${assetId}/profile`, { method: 'PUT', body: JSON.stringify({ profile_id: profileId || null }) });
      setAssetProfiles(prev => {
        const prof = profileId ? profiles.find(p => p.id === profileId) : null;
        return { ...prev, [assetId]: prof };
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving('');
    }
  };

  const filtered = assets.filter(a =>
    !search || a.hostname?.toLowerCase().includes(search.toLowerCase()) || a.user_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      {error && (
        <div style={{ background: 'var(--red-dim)', color: 'var(--red)', padding: '10px 14px', borderRadius: 8, marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
          {error}
          <button onClick={() => setError('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red)' }}>✕</button>
        </div>
      )}
      <div style={{ marginBottom: 16 }}>
        <input className="form-input" placeholder="Buscar equipo..." value={search}
          onChange={e => setSearch(e.target.value)} style={{ width: 280 }} />
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--gray-500)' }}>Cargando...</div>
      ) : (
        <div className="table-wrap">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                <th style={{ padding: '10px 12px', fontWeight: 500, color: 'var(--gray-500)' }}>Hostname</th>
                <th style={{ padding: '10px 12px', fontWeight: 500, color: 'var(--gray-500)' }}>Usuario</th>
                <th style={{ padding: '10px 12px', fontWeight: 500, color: 'var(--gray-500)' }}>Perfil asignado</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={3} style={{ padding: 24, textAlign: 'center', color: 'var(--gray-500)' }}>Sin equipos.</td></tr>
              ) : filtered.map(a => {
                const currentProfile = assetProfiles[a.id];
                return (
                  <tr key={a.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 500 }}>{a.hostname}</td>
                    <td style={{ padding: '10px 12px', color: 'var(--gray-500)' }}>{a.user_name || '—'}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <select className="form-input"
                        value={currentProfile?.id || ''}
                        onChange={e => assignProfile(a.id, e.target.value)}
                        disabled={saving === a.id}
                        style={{ minWidth: 200 }}>
                        <option value="">Sin perfil</option>
                        {profiles.map(p => (
                          <option key={p.id} value={p.id}>{p.name} ({p.profile_type})</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────── Desviaciones tab ──────────────────────── */
function DeviationsTab() {
  const [deviations, setDeviations] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [filterStatus, setFilterStatus] = useState('open');
  const [filterProfile, setFilterProfile] = useState('');
  const [analyzeResult, setAnalyzeResult] = useState(null);
  const [resolving, setResolving] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ status: filterStatus });
    if (filterProfile) params.append('profile_id', filterProfile);
    Promise.all([
      apiFetch(`/deviations?${params}`),
      apiFetch('/profiles'),
    ]).then(([d, p]) => {
      setDeviations(Array.isArray(d) ? d : []);
      setProfiles(Array.isArray(p) ? p : []);
    }).catch(e => setError(e.message)).finally(() => setLoading(false));
  }, [filterStatus, filterProfile]);

  useEffect(() => { load(); }, [load]);

  const analyze = async () => {
    setAnalyzing(true);
    setAnalyzeResult(null);
    try {
      const result = await apiFetch('/analyze', { method: 'POST' });
      setAnalyzeResult(result);
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setAnalyzing(false);
    }
  };

  const resolve = async (id) => {
    setResolving(id);
    try {
      await apiFetch(`/deviations/${id}/resolve`, { method: 'PATCH' });
      setDeviations(prev => prev.filter(d => d.id !== id));
    } catch (e) {
      setError(e.message);
    } finally {
      setResolving('');
    }
  };

  return (
    <div>
      {error && (
        <div style={{ background: 'var(--red-dim)', color: 'var(--red)', padding: '10px 14px', borderRadius: 8, marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
          {error}
          <button onClick={() => setError('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red)' }}>✕</button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <select className="form-input" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ flex: '0 0 150px' }}>
          <option value="open">Abiertas</option>
          <option value="resolved">Resueltas</option>
        </select>
        <select className="form-input" value={filterProfile} onChange={e => setFilterProfile(e.target.value)} style={{ flex: '0 0 220px' }}>
          <option value="">Todos los perfiles</option>
          {profiles.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <div style={{ flex: 1 }} />
        {analyzeResult && (
          <span style={{ fontSize: 12, color: 'var(--gray-500)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <CheckCircle size={12} style={{ color: 'var(--green)' }} />
            {analyzeResult.message}
          </span>
        )}
        <button className="btn btn-primary" onClick={analyze} disabled={analyzing}>
          <RefreshCw size={14} style={{ marginRight: 6 }} />
          {analyzing ? 'Analizando...' : 'Analizar ahora'}
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--gray-500)' }}>Cargando desviaciones...</div>
      ) : (
        <div className="table-wrap">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                <th style={{ padding: '10px 12px', fontWeight: 500, color: 'var(--gray-500)' }}>Hostname</th>
                <th style={{ padding: '10px 12px', fontWeight: 500, color: 'var(--gray-500)' }}>Software</th>
                <th style={{ padding: '10px 12px', fontWeight: 500, color: 'var(--gray-500)' }}>Versión</th>
                <th style={{ padding: '10px 12px', fontWeight: 500, color: 'var(--gray-500)' }}>Tipo</th>
                <th style={{ padding: '10px 12px', fontWeight: 500, color: 'var(--gray-500)' }}>Severidad</th>
                <th style={{ padding: '10px 12px', fontWeight: 500, color: 'var(--gray-500)' }}>Perfil</th>
                <th style={{ padding: '10px 12px', fontWeight: 500, color: 'var(--gray-500)' }}>Detectado</th>
                {filterStatus === 'open' && <th></th>}
              </tr>
            </thead>
            <tbody>
              {deviations.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ padding: 24, textAlign: 'center', color: 'var(--gray-500)' }}>
                    {filterStatus === 'open'
                      ? 'Sin desviaciones abiertas. Haz clic en "Analizar ahora" para iniciar.'
                      : 'Sin desviaciones resueltas.'}
                  </td>
                </tr>
              ) : deviations.map(d => (
                <tr key={d.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '10px 12px', fontWeight: 500 }}>{d.hostname}</td>
                  <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: 12 }}>{d.software_name}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--gray-500)', fontSize: 12 }}>{d.software_version || '—'}</td>
                  <td style={{ padding: '10px 12px' }}>
                    {d.deviation_type === 'unauthorized' ? (
                      <span style={{ background: 'var(--red-dim)', color: 'var(--red)', padding: '2px 10px', borderRadius: 12, fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Ban size={11} />No autorizado
                      </span>
                    ) : (
                      <span style={{ background: 'var(--amber-dim)', color: 'var(--amber)', padding: '2px 10px', borderRadius: 12, fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <AlertTriangle size={11} />Faltante
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '10px 12px' }}><SeverityBadge severity={d.severity} /></td>
                  <td style={{ padding: '10px 12px' }}>
                    {d.profile_color && (
                      <span style={{ background: d.profile_color + '20', color: d.profile_color, border: `1px solid ${d.profile_color}40`, padding: '2px 10px', borderRadius: 12, fontSize: 12 }}>
                        {d.profile_name}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '10px 12px', color: 'var(--gray-500)' }}>
                    {d.detected_at ? new Date(d.detected_at).toLocaleDateString('es-AR') : '—'}
                  </td>
                  {filterStatus === 'open' && (
                    <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                      <button
                        onClick={() => resolve(d.id)}
                        disabled={resolving === d.id}
                        style={{ background: 'var(--green-dim)', color: 'var(--green)', border: '1px solid #86efac', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <CheckCircle size={12} />Resolver
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────── Main component ────────────────────────── */
export default function Baseline() {
  const [tab, setTab] = useState('profiles');

  const tabs = [
    { id: 'profiles', label: 'Perfiles' },
    { id: 'assignments', label: 'Asignaciones' },
    { id: 'deviations', label: 'Desviaciones' },
  ];

  return (
    <div style={{ padding: '24px 28px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <Shield size={22} style={{ color: 'var(--blue)' }} />
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Baseline de Software</h1>
          <p style={{ fontSize: 13, color: 'var(--gray-500)', margin: 0 }}>Perfiles blacklist/whitelist y control de desviaciones</p>
        </div>
      </div>

      <SummaryBar />

      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid var(--border)' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{
              padding: '8px 18px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13,
              fontWeight: tab === t.id ? 600 : 400,
              color: tab === t.id ? 'var(--blue)' : 'var(--gray-500)',
              borderBottom: tab === t.id ? '2px solid var(--blue)' : '2px solid transparent',
              marginBottom: -1, transition: 'all 0.15s',
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'profiles' && <ProfilesTab />}
      {tab === 'assignments' && <AssignmentsTab />}
      {tab === 'deviations' && <DeviationsTab />}
    </div>
  );
}
